import { storage } from '@/lib/utils/storage';

/**
 * Lo que la empresa tiene contratado — el "techo" — guardado del lado del cliente.
 *
 * 🔴 Para qué sirve: **saber de quién es la puerta cerrada.** Hay dos capas que
 * pueden esconder un módulo, y el usuario ve la misma pantalla en los dos casos:
 *
 *   · el **rol** no lo tiene       → lo resuelve el administrador de su empresa
 *   · la **empresa** no lo compró  → lo resuelve DATEC
 *
 * `menuItems` es la intersección de las dos y por eso no alcanza para distinguir:
 * un vendedor que abría /permisos leía "Este módulo no está incluido", que
 * significa "hablá con DATEC", cuando su propio administrador se lo podía dar en
 * diez segundos. Lo mandaba a reclamarle a la persona equivocada.
 *
 * Vive en `storage` y no en el store de sesión a propósito: se escribe en los
 * mismos cinco lugares donde ya se escribe `user_menu_items` y se lee en uno
 * solo. Meterlo en el estado obligaría a hacerle lugar en el login, el registro,
 * el refresco, la impersonación y la restauración de respaldo, para un dato que
 * solo se consulta al bloquear una ruta.
 */
const TECHO_KEY = 'tenant_modules';

/**
 * Productos que se ven sólo si la empresa los tiene marcados en el contrato,
 * aunque no haya techo configurado. Espejo de `MODULOS_POR_CONTRATO` en
 * `packages/backend/src/utils/permissions.ts`: si cambia allá, cambiarlo acá.
 */
export const MODULOS_POR_CONTRATO: readonly string[] = ['menu:pricer-datec'];

/** Guarda el techo. `undefined` del servidor se guarda como `null` — "no sé". */
export function guardarTechoDeLaEmpresa(modulos: string[] | null | undefined): void {
  storage.set(TECHO_KEY, modulos ?? null);
}

/**
 * El techo, o `null` si la empresa no tiene uno configurado **o si no lo sabemos**
 * (sesión vieja, guardada antes de que esto existiera).
 *
 * Los dos casos se tratan igual a propósito: sin techo no hay módulo que se pueda
 * atribuir al plan, así que la explicación correcta siempre es el rol.
 */
export function techoDeLaEmpresa(): string[] | null {
  const guardado = storage.get<string[] | null>(TECHO_KEY);
  return Array.isArray(guardado) && guardado.length > 0 ? guardado : null;
}

/*
  Techo de la empresa cuyos ROLES se están editando (D4, 16-oct).

  La pantalla de Roles y permisos ofrece los productos por contrato (el grupo «Pricer Datec») según lo que la EMPRESA
  tiene contratado, no según lo que tiene quien edita. Para casi todos es lo mismo (`techoDeLaEmpresa`), pero el
  PLATFORM_ADMIN no tiene techo en su sesión (`tenantModules: null`), y con eso la pantalla le escondía el producto de
  una empresa que sí lo tiene. Para él el techo real se pide al servidor (`/permissions/company-modules`) y queda acá,
  en memoria: es un dato de la pantalla, no de la sesión, y no sobrevive a recargar.
  `undefined` = no se pidió (o no hace falta): vale el de la sesión.
*/
let techoPedidoAlServidor: string[] | null | undefined = undefined;

export function fijarTechoParaEditarRoles(modulos: string[] | null | undefined): void {
  techoPedidoAlServidor = Array.isArray(modulos) && modulos.length > 0 ? modulos : null;
}

export function olvidarTechoParaEditarRoles(): void {
  techoPedidoAlServidor = undefined;
}

/** El techo de la empresa que se está editando: el pedido al servidor si lo hay, si no el de la sesión. */
export function techoParaEditarRoles(): string[] | null {
  return techoPedidoAlServidor !== undefined ? techoPedidoAlServidor : techoDeLaEmpresa();
}

/**
 * Por qué está cerrado un módulo que el usuario no ve.
 *
 * Solo se llama cuando ya se sabe que no lo ve; acá se decide **qué decirle**.
 */
export function motivoDelBloqueo(menuKey: string): 'rol' | 'no-incluido' {
  const techo = techoDeLaEmpresa();
  if (techo === null) {
    // Sin techo no se puede atribuir al plan, salvo un producto por contrato:
    // ése no se ve a menos que esté marcado.
    return MODULOS_POR_CONTRATO.includes(`menu:${menuKey}`) ? 'no-incluido' : 'rol';
  }
  return techo.includes(`menu:${menuKey}`) ? 'rol' : 'no-incluido';
}

/**
 * ¿La empresa tiene ALGUNO de estos módulos en su techo? Es la misma cuenta que hace el servidor en
 * `techoPorPrefijo` (2.7c): sin techo configurado (o sin saber cuál es) la respuesta es sí, porque el
 * servidor tampoco corta. Sirve para esconder lo que el servidor contestaría con 403 `MODULO_NO_INCLUIDO`
 * (una pestaña, un botón), sin mirar el rol: el rol lo deciden las pantallas y los routers.
 */
export function empresaTieneAlgunModulo(modulos: readonly string[]): boolean {
  const techo = techoDeLaEmpresa();
  if (techo === null) return true;
  return modulos.some((m) => techo.includes(m));
}

/**
 * Los módulos con los que el servidor deja usar `/api/integrations` (Integraciones, y Cotiza y Datos Maestros
 * que leen sus configuraciones). ESPEJO de la regla `integrations` en `packages/backend/src/config/modulos-por-prefijo.ts`:
 * si cambia allá, cambiarlo acá.
 */
export const MODULOS_QUE_USAN_INTEGRACIONES: readonly string[] = [
  'menu:integrations',
  'menu:master-data',
  'menu:quotes',
  'menu:nueva-cotizacion',
  'menu:approvals',
  'menu:customers',
  'menu:contracts',
  'menu:opportunities',
  'menu:pricer-datec',
  'menu:pricer-solar',
];
